import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Eye, EyeOff, Mail, ShieldCheck } from 'lucide-react';
import { useAuth } from '../components/auth/AuthContext';
import AuthSplit from '../components/auth/AuthSplit';
import { rememberPendingFreeCourse } from '../lib/free-enrollment';

const SignUp = () => {
  const [searchParams] = useSearchParams();
  const requestedCourse = searchParams.get('course') || '';
  const requestedEmail = searchParams.get('email') || '';
  const [formData, setFormData] = useState({
    name: '',
    email: requestedEmail,
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const { signup, resendVerification } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (requestedCourse) rememberPendingFreeCourse(requestedCourse);
  }, [requestedCourse]);

  useEffect(() => {
    if (requestedEmail) {
      setFormData((current) => (current.email ? current : { ...current, email: requestedEmail }));
    }
  }, [requestedEmail]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setNotice('');

    if (formData.password.length < 8) {
      setError('Use at least 8 characters for your password.');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setBusy(true);
    try {
      const { needsConfirmation: pending } = await signup({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        courseId: requestedCourse || undefined,
      });
      if (pending) {
        setNeedsConfirmation(true);
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create account');
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setNotice('');
    try {
      await resendVerification(formData.email);
      setNotice('Confirmation email sent. Check your inbox and spam folder.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not resend the email.');
    }
  };

  if (needsConfirmation) {
    return (
      <AuthSplit
        eyebrow="Student account / Email confirmation"
        title="Check your inbox."
        lede={
          <>
            We sent a confirmation link to <strong className="font-semibold text-slate-900">{formData.email}</strong>.
            Open it to activate your student account, then sign in.
          </>
        }
        notes={
          <ol className="space-y-3">
            {[
              ['01', 'Open the email from LSEM.'],
              ['02', 'Follow the confirmation link.'],
              [
                '03',
                requestedCourse
                  ? 'Sign in — your free programme is added automatically when your account is confirmed.'
                  : 'Sign in and choose your first programme.',
              ],
            ].map(([n, text]) => (
              <li key={n} className="grid grid-cols-[28px_minmax(0,1fr)] gap-2">
                <span className="text-[9px] font-semibold text-blue-600">{n}</span>
                <span>{text}</span>
              </li>
            ))}
          </ol>
        }
        footer={
          <p>
            Already confirmed?{' '}
            <Link to={`/login?email=${encodeURIComponent(formData.email)}`} className="auth-link">
              Sign in to your account
            </Link>
            .
          </p>
        }
      >
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_24px_70px_-48px_rgba(15,40,90,0.45)] sm:p-7">
          <div className="flex items-start gap-3 border-b border-slate-100 pb-5">
            <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-blue-50">
              <Mail className="h-5 w-5 text-blue-600" strokeWidth={1.6} />
            </span>
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.1em] text-amber-700">
                Awaiting email
              </span>
              <p className="mt-2 text-[12.5px] leading-5 text-slate-500">
                Confirmation keeps password recovery and certificate records attached to the right person.
              </p>
            </div>
          </div>

          {error && (
            <div className="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[12.5px] leading-5 text-red-700" role="alert">
              {error}
            </div>
          )}
          {notice && (
            <div className="mt-5 flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[12.5px] leading-5 text-emerald-700" role="status">
              <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none" strokeWidth={1.7} />
              {notice}
            </div>
          )}

          <div className="mt-6 space-y-2.5">
            <Link to={`/login?email=${encodeURIComponent(formData.email)}`} className="auth-btn-primary">
              Sign in to your account
              <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
            </Link>
            <button type="button" onClick={() => void handleResend()} className="auth-btn-secondary">
              Resend confirmation email
            </button>
          </div>

          <p className="mt-5 text-[11.5px] leading-5 text-slate-500">
            Wrong address?{' '}
            <Link to="/signup" className="auth-link">
              Start a new student account
            </Link>
            .
          </p>
        </div>
      </AuthSplit>
    );
  }

  return (
    <AuthSplit
      eyebrow="Admissions / New student account"
      title="Create your student account."
      lede="One account holds your programme, lessons, notes, assessments and certificate record."
      notes={
        <ol className="space-y-4">
          {[
            ['01', 'Create your account', 'Name, email and phone.'],
            ['02', 'Choose a programme', 'Free pathways open immediately. Paid enrolment runs through Paystack.'],
            ['03', 'Work to clearance', 'Complete every lesson to issue a certificate employers can verify.'],
          ].map(([n, title, copy]) => (
            <li key={n} className="grid grid-cols-[28px_minmax(0,1fr)] gap-2">
              <span className="text-[9px] font-semibold text-blue-600">{n}</span>
              <span>
                <span className="block text-[12.5px] font-semibold text-slate-800">{title}</span>
                <span className="mt-0.5 block text-[11.5px] leading-5 text-slate-500">{copy}</span>
              </span>
            </li>
          ))}
        </ol>
      }
      footer={
        <p>
          Already enrolled?{' '}
          <Link to="/login" className="auth-link">
            Sign in
          </Link>
          .
        </p>
      }
    >
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_24px_70px_-48px_rgba(15,40,90,0.45)] sm:p-7">
        <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-4">
          <h2 className="text-[17px] font-semibold tracking-[-0.015em] text-slate-950">Your details</h2>
          <span className="inline-flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-[0.1em] text-slate-400">
            <ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.7} /> Required
          </span>
        </div>

        {error && (
          <div className="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[12.5px] leading-5 text-red-700" role="alert">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-5 space-y-4">
          <div>
            <label htmlFor="signup-name" className="auth-label">Full name</label>
            <input
              id="signup-name"
              type="text"
              required
              autoComplete="name"
              placeholder="e.g. Ada Okafor"
              className="auth-field"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="signup-email" className="auth-label">Email address</label>
              <input
                id="signup-email"
                type="email"
                required
                autoComplete="email"
                placeholder="you@example.com"
                className="auth-field"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="signup-phone" className="auth-label">Phone number</label>
              <input
                id="signup-phone"
                type="tel"
                required
                autoComplete="tel"
                placeholder="+234…"
                className="auth-field"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label htmlFor="signup-password" className="auth-label">Password</label>
            <div className="relative">
              <input
                id="signup-password"
                type={showPassword ? 'text' : 'password'}
                required
                autoComplete="new-password"
                placeholder="At least 8 characters"
                className="auth-field pr-12"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-slate-400 transition-colors hover:text-slate-700"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="h-4 w-4" strokeWidth={1.7} /> : <Eye className="h-4 w-4" strokeWidth={1.7} />}
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="signup-confirm" className="auth-label">Confirm password</label>
            <div className="relative">
              <input
                id="signup-confirm"
                type={showConfirmPassword ? 'text' : 'password'}
                required
                autoComplete="new-password"
                placeholder="Repeat your password"
                className="auth-field pr-12"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-slate-400 transition-colors hover:text-slate-700"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? 'Hide password confirmation' : 'Show password confirmation'}
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" strokeWidth={1.7} /> : <Eye className="h-4 w-4" strokeWidth={1.7} />}
              </button>
            </div>
          </div>

          {requestedCourse && (
            <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-[11.5px] leading-5 text-blue-800">
              Your free programme is reserved. It will be added to your student account automatically when you confirm
              your email and sign in.
            </div>
          )}

          <button type="submit" disabled={busy} className="auth-btn-primary mt-1">
            {busy ? 'Creating account…' : 'Create student account'}
          </button>
        </form>

        <p className="mt-4 text-[10.5px] leading-5 text-slate-400">
          By creating an account you agree to LSEM using these details to administer your enrolment and certificate.
        </p>
      </div>
    </AuthSplit>
  );
};

export default SignUp;
